import { useCallback } from 'react';
import { useQuickAddToBag } from '../../../../../hooks/useQuickAddToBag';
import {
    BloomreachProductType,
    type SearchProduct,
} from '../../../hooks/use-search/use-search-types';
import { mapSearchProductToProduct } from '../../../utils/search-page-utils';
import type { Product } from '../../../../../bff-api/products/model/Product';
import type { UseQuickAddToBagResult } from '../../../../../hooks/useQuickAddToBag';
import type { PDPLocationState } from '../../../../pdp/types';
import type { SearchProductTileProps } from '../../search-listing/SearchListing';
import type { SelectedVariant } from '../hardgood/ProductTileHardGood';

type QuickAddItem = Omit<
    SearchProductTileProps,
    'source' | 'onClick' | 'shippingOptionType' | 'loading' | 'fetchPriority'
> &
    SearchProduct;

interface UseProductTileQuickAddToBagParams {
    item: QuickAddItem;
    selection: SelectedVariant;
    activeVariantId?: string;
    shippingOptionType?: SearchProductTileProps['shippingOptionType'];
    source: SearchProductTileProps['source'];
}

interface ProductTileQuickAddToBagResult {
    handleQuickAdd: () => Promise<void>;
    isLoading: UseQuickAddToBagResult['isLoading'];
    pdpLocationState: PDPLocationState | undefined;
}

export const useProductTileQuickAddToBag = ({
    item,
    selection,
    activeVariantId,
    shippingOptionType,
    source,
}: UseProductTileQuickAddToBagParams): ProductTileQuickAddToBagResult => {
    const {
        handleQuickAdd: quickAdd,
        isLoading,
        pdpLocationState,
    } = useQuickAddToBag({
        shippingOptionType,
        source,
    });

    const handleQuickAdd = useCallback(async (): Promise<void> => {
        const buildProduct = (): Product => {
            const buildSearchProduct = (): SearchProduct => {
                switch (item.productType) {
                    case BloomreachProductType.recipe:
                        return {
                            ...item,
                            productType: BloomreachProductType.recipe,
                        };
                    case BloomreachProductType.culinary:
                        if (!item.searchPrices) {
                            throw new Error('Missing search prices for culinary search product');
                        }
                        return {
                            ...item,
                            productType: BloomreachProductType.culinary,
                            searchPrices: item.searchPrices,
                        };
                    case BloomreachProductType.hardgood: {
                        const resolvedSearchPrices = item.searchPrices ?? selection.searchPrices;

                        if (!resolvedSearchPrices) {
                            throw new Error('Missing search prices for hardgood search product');
                        }

                        const hardgoodProduct: Extract<
                            SearchProduct,
                            { productType: BloomreachProductType.hardgood }
                        > = {
                            ...item,
                            productType: BloomreachProductType.hardgood,
                            searchPrices: resolvedSearchPrices,
                        };

                        if (!activeVariantId) {
                            return hardgoodProduct;
                        }

                        return {
                            ...hardgoodProduct,
                            id: selection.id,
                            c_pwaUrl: selection.c_pwaUrl,
                            c_imageUrl: selection.c_imageUrl,
                            c_badge: selection.c_badge,
                            searchPrices: selection.searchPrices,
                            c_promotionHTMLs: selection.c_promotionHTMLs ?? item.c_promotionHTMLs,
                            c_variants: hardgoodProduct.c_variants.map((variant) =>
                                variant.id === selection.id
                                    ? {
                                          ...variant,
                                          c_pwaUrl: selection.c_pwaUrl,
                                          c_imageUrl: selection.c_imageUrl,
                                          c_badge: selection.c_badge,
                                          searchPrices: selection.searchPrices,
                                          c_promotionHTMLs: selection.c_promotionHTMLs,
                                          sku_images: selection.sku_images ?? variant.sku_images,
                                      }
                                    : variant,
                            ),
                        };
                    }
                    default:
                        throw new Error('Unsupported search product type');
                }
            };

            return mapSearchProductToProduct(buildSearchProduct());
        };

        await quickAdd(buildProduct);
    }, [activeVariantId, item, quickAdd, selection]);

    return {
        handleQuickAdd,
        isLoading,
        pdpLocationState,
    };
};
